import { FC, useEffect, useState, useCallback, useMemo } from 'react';
import {
    Menu,
    ActionIcon,
    Text,
    Group,
    Stack,
    Badge,
    Tooltip,
    Divider,
    Box,
    Switch
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconDotsVertical, IconFile } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useGetMasterSettings } from '@/core/api/query/master.query';
import { getDocumentPrintApi } from '@/core/api/api.service';
import Loading from '@/shared/components/loading/loading';

interface DocumentMenuProps {
    docType: string;
    docNo: string;
    onBusyChange?: (busy: boolean) => void;
}

interface PrintSetting {
    docSrNo: number;
    description: string;
    label?: string;
    format: string;
}

interface DocumentOperation {
    type: 'copy' | 'revise';
    label: string;
    description: string;
    icon: React.ReactNode;
    isEnabled: boolean;
    requiresConfirmation: boolean;
    confirmationMessage?: string;
}

const PrintMenu: FC<{ docType: string; docNo?: string }> = ({ docType, docNo }) => {
    const [printSettings, setPrintSettings] = useState<PrintSetting[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { data, isLoading: isDataLoading } = useGetMasterSettings(docType ?? '');

    const [autoDownloadItems, setAutoDownloadItems] = useState<Set<number>>(new Set());
    const storageKey = useMemo(() => `autoDownloadItems-${docType ?? ''}`, [docType]);

    useEffect(() => {
        if (data?.data?.metadata?.printSettings) {
            setPrintSettings(data.data.metadata.printSettings);
        }
    }, [data]);

    useEffect(() => {
        try {
            const storedItems = localStorage.getItem(storageKey);
            if (storedItems) {
                setAutoDownloadItems(new Set(JSON.parse(storedItems)));
            } else {
                // Clear auto-download items when document changes
                setAutoDownloadItems(new Set());
            }
        } catch (error) {
            console.warn('Failed to load auto-download preferences:', error);
            setAutoDownloadItems(new Set());
        }
    }, [storageKey]);

    // Cleanup old storage keys on mount (remove document-specific keys)
    useEffect(() => {
        if (docType) {
            try {
                const keysToRemove: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    // Remove old document-specific keys but keep the new docType-only keys
                    if (key && key.startsWith(`autoDownloadItems-${docType}-`) && key !== `autoDownloadItems-${docType}`) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
            } catch (error) {
                console.warn('Failed to cleanup old storage keys:', error);
            }
        }
    }, [docType]);


    const handleDownload = useCallback(async (item: PrintSetting) => {
        if (!docNo) return;

        setIsLoading(true);
        try {
            notifications.show({
                id: `download-${item.docSrNo}`,
                title: 'Downloading',
                message: `Preparing ${item.description}...`,
                loading: true,
                autoClose: false,
                position: 'top-center',
            });

            await getDocumentPrintApi(docType ?? '', item.docSrNo, `docNo=${docNo}`);

            notifications.update({
                id: `download-${item.docSrNo}`,
                title: 'Download Complete',
                message: `${item.description} downloaded successfully`,
                color: 'green',
                autoClose: 3000,
                position: 'top-center',
            });
        } catch (error) {
            console.error('Download failed:', error);
            notifications.update({
                id: `download-${item.docSrNo}`,
                title: 'Download Failed',
                message: `Failed to download ${item.description}`,
                color: 'red',
                autoClose: 5000,
                position: 'top-center',
            });
        } finally {
            setIsLoading(false);
        }
    }, [docType, docNo]);

    // Utility function to save auto-download preferences
    const saveAutoDownloadPreferences = useCallback((items: Set<number>) => {
        try {
            localStorage.setItem(storageKey, JSON.stringify([...items]));
            return true;
        } catch (error) {
            console.warn('Failed to save auto-download preferences:', error);
            return false;
        }
    }, [storageKey]);


    const toggleAutoDownload = useCallback((docSrNo: number) => {
        setAutoDownloadItems(prev => {
            const newSet = new Set(prev);
            const wasEnabled = newSet.has(docSrNo);

            if (wasEnabled) {
                newSet.delete(docSrNo);
            } else {
                newSet.add(docSrNo);
            }

            const saveSuccess = saveAutoDownloadPreferences(newSet);

            if (saveSuccess) {
                // Show feedback notification
                const item = printSettings.find(p => p.docSrNo === docSrNo);
                notifications.show({
                    title: wasEnabled ? 'Auto-Download Disabled' : 'Auto-Download Enabled',
                    message: `${item?.description || 'Print option'} will ${wasEnabled ? 'not' : 'now'} download automatically for all ${docType} documents`,
                    color: wasEnabled ? 'orange' : 'green',
                    autoClose: 2000,
                    position: 'top-center',
                });
            } else {
                notifications.show({
                    title: 'Save Failed',
                    message: 'Failed to save auto-download preference',
                    color: 'red',
                    autoClose: 3000,
                    position: 'top-center',
                });
            }

            return newSet;
        });
    }, [saveAutoDownloadPreferences, printSettings, docType]);

    const isDownloadDisabled = !docNo || isLoading;

    if (isDataLoading) {
        return (
            <Box p="xs">
                <Group justify="center" gap="xs">
                    <Loading variant="inline" size="xs" label="Loading..." labelClassName="text-xs text-gray-500" />
                </Group>
            </Box>
        );
    }

    if (!printSettings?.length) return null;

    return (
        <>
            <Divider my="xs" />
            <Menu.Label className="px-2 py-1 text-xs font-medium text-gray-600 flex items-center justify-between">
                <span>Print</span>
                {autoDownloadItems.size > 0 && (
                    <Badge size="xs" variant="light" color="blue">
                        {autoDownloadItems.size} auto
                    </Badge>
                )}
            </Menu.Label>
            <Stack gap={4}>
                {printSettings.map((item) => {
                    const isAutoEnabled = autoDownloadItems.has(item.docSrNo);
                    return (
                        <Menu.Item
                            key={item.docSrNo}
                            disabled={isDownloadDisabled}

                            className="px-2 py-1.5 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            <Text onClick={() => !isDownloadDisabled && handleDownload(item)} size="sm" fw={500} truncate>
                                {item.label || item.description}
                            </Text>
                            <Group gap="xs" align="center" justify="space-between">
                                <Badge
                                    size="xs"
                                    variant="light"
                                    color={isAutoEnabled ? "blue" : "gray"}
                                    className={isAutoEnabled ? "ring-1 ring-blue-200" : ""}
                                >
                                    {item.format?.toUpperCase() || 'PDF'}
                                </Badge>
                                <Tooltip label={isAutoEnabled ? 'Disable auto-download' : 'Enable auto-download'}>
                                    <Box onClick={(e) => e.stopPropagation()}>
                                        <Switch
                                            size="xs"
                                            checked={isAutoEnabled}
                                            onChange={() => toggleAutoDownload(item.docSrNo)}
                                            color="blue"
                                            className="cursor-pointer"
                                        />
                                    </Box>
                                </Tooltip>
                            </Group>
                        </Menu.Item>
                    );
                })}
            </Stack>
        </>
    );
};

export const DocumentMenu: FC<DocumentMenuProps> = ({ docType, docNo, onBusyChange }) => {
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);

    const documentOperations = useMemo((): DocumentOperation[] => [
        {
            type: 'copy',
            label: 'Create Copy',
            description: 'Duplicate this document',
            icon: <IconCopy className="h-4 w-4" stroke={1.8} />,
            isEnabled: (docType === 'SE' || docType === 'USR') && !!docNo,
            requiresConfirmation: true,
            confirmationMessage: docType === 'USR'
                ? 'Are you sure you want to copy this USR? This will create a new draft with the same data.'
                : 'Are you sure you want to copy this document? This will create a duplicate.'
        },
        {
            type: 'revise',
            label: 'Create Revision',
            description: 'Create a new revision',
            icon: <IconFile className="h-4 w-4" stroke={1.8} />,
            isEnabled: docType === 'SQ' && !!docNo,
            requiresConfirmation: true,
            confirmationMessage: 'Are you sure you want to revise this SQ? This will create a new revision and disable the current document.'
        }
    ], [docType, docNo]);


    const handleUSRCopy = useCallback(() => {
        if (isProcessing) return;

        setIsProcessing(true);
        onBusyChange?.(true);

        try {
            navigate(`/form/${docType}/add`, { replace: true });

            notifications.show({
                title: 'User Copied',
                message: 'User data copied into a new draft. Please update unique fields before saving.',
                color: 'green',
                autoClose: 4000,
                position: 'top-center',
            });
        } catch (error) {
            console.error('Error copying USR:', error);
            notifications.show({
                title: 'Copy Failed',
                message: 'Failed to copy user data. Please try again.',
                color: 'red',
                autoClose: 5000,
                position: 'top-center',
            });
        } finally {
            setIsProcessing(false);
            onBusyChange?.(false);
        }
    }, [docType, docNo, navigate, isProcessing, onBusyChange]);

    const openConfirmModal = useCallback((operation: DocumentOperation) => {
        modals.openConfirmModal({
            title: `Confirm ${operation.label}`,
            children: (
                <Stack gap="sm">
                    <Text size="sm">{operation.confirmationMessage}</Text>
                    {operation.type === 'copy' && docType === 'USR' && (
                        <Text size="xs" c="dimmed">
                            Note: This will copy all user data to a new draft form.
                        </Text>
                    )}
                </Stack>
            ),
            labels: { confirm: 'Confirm', cancel: 'Cancel' },
            confirmProps: {
                color: 'blue',
                loading: isProcessing
            },
            cancelProps: {
                disabled: isProcessing
            },
            onConfirm: () => {
                if (operation.type === 'copy' && docType === 'USR') {
                    handleUSRCopy();
                } 
            },
        });
    }, [isProcessing, docType, handleUSRCopy]);

    const handleOperationClick = useCallback((operation: DocumentOperation) => {
        if (!operation.isEnabled || isProcessing) return;

        if (operation.requiresConfirmation) {
            openConfirmModal(operation);
        } else {
            if (operation.type === 'copy' && docType === 'USR') {
                handleUSRCopy();
            } 
        }
    }, [openConfirmModal, handleUSRCopy, docType, isProcessing]);

    return (
        <Menu
            position="bottom-end"
            shadow="sm"
            closeOnItemClick={false}
            radius="md"
            width={240}
        >
            <Menu.Target>
                <Tooltip label="Document Actions" position="bottom">
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        loading={isProcessing}
                        className="hover:bg-gray-100 hover:text-gray-700 transition-colors"
                    >
                        <IconDotsVertical className="h-4 w-4" stroke={1.8} />
                    </ActionIcon>
                </Tooltip>
            </Menu.Target>

            <Menu.Dropdown className="p-1">
                <Menu.Label className="px-2 py-1 text-xs font-medium text-gray-600">
                    Actions
                </Menu.Label>

                <Stack gap={2}>
                    {documentOperations.map((operation) => (
                        <Menu.Item
                            key={operation.type}
                            disabled={!operation.isEnabled || isProcessing}
                            leftSection={
                                <Box className={`w-4 h-4 ${operation.isEnabled ? 'text-gray-600' : 'text-gray-400'}`}>
                                    {operation.icon}
                                </Box>
                            }
                            onClick={() => handleOperationClick(operation)}
                            className={`px-2 py-1.5 rounded-md transition-colors ${operation.isEnabled
                                ? 'hover:bg-gray-50 text-gray-700'
                                : 'opacity-50 cursor-not-allowed text-gray-400'
                                }`}
                        >
                            <Box>
                                <Text size="sm" fw={500}>
                                    {operation.label}
                                </Text>
                                <Text size="xs" c="dimmed" className="mt-0.5">
                                    {operation.description}
                                </Text>
                            </Box>
                        </Menu.Item>
                    ))}
                </Stack>

                <PrintMenu docType={docType} docNo={docNo} />
            </Menu.Dropdown>
        </Menu>
    );
};

export default DocumentMenu;


